import useDateLocale from '@/common/hooks/useDateLocale'
import { MeetingContext } from '@/meeting/contexts/MeetingContext'
import { Box, Flex, HStack, Text } from '@chakra-ui/react'
import { format } from 'date-fns'
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useProposal } from '../contexts/ProposalContext'
import ProposalManageMenu from './ProposalManageMenu'
import ProposalNonVotersNotice from './ProposalNonVotersNotice'
import ProposalResolveMeetingButtons from './ProposalResolveMeetingButtons'
import ProposalVoteButtons from './ProposalVoteButtons'

// In-progress proposal: decision mode label, vote buttons, progress and the
// manage menu.
export default function ProposalVoteSection() {
  const { t } = useTranslation()
  const { activity, votes, state, canManage } = useProposal()
  const dateLocale = useDateLocale()

  // Decided in a meeting: there is no vote to cast, none to count, and nobody
  // who "cannot vote". Offering the buttons anyway would collect opinions the
  // resolution never reads — worse than showing nothing (D6, 06/08/2026).
  const decidedInMeeting = activity.data.decisionMode === 'meeting'

  // ⚠️ SCREEN LOCK ONLY, never a protection (D6, B6). The decision gestures are
  // offered while the proposal is read from inside a meeting, and hidden
  // elsewhere, because deciding on behalf of a circle outside its meeting makes
  // no sense. The SERVER cannot enforce it: a meeting's tensions live in a json
  // column (meeting_step.data) with no foreign key, so no row-level rule can
  // follow that path. Do not read this as a guarantee, and do not build on it.
  const meetingState = useContext(MeetingContext)
  const readFromMeeting = !!meetingState?.meeting

  return (
    <Box>
      <Text
        fontSize="sm"
        fontWeight="medium"
        color="gray.500"
        _dark={{ color: 'gray.300' }}
        mb={2}
      >
        {t(`ThreadActivityProposal.voteMode_${activity.data.decisionMode}`)}
      </Text>

      {/* The deadline is read, never changed: it is set by the organization */}
      {activity.data.resolutionDate && (
        <Text
          fontSize="sm"
          color="gray.500"
          _dark={{ color: 'gray.300' }}
          mb={2}
        >
          {t('ThreadActivityProposal.deadline', {
            date: format(new Date(activity.data.resolutionDate), 'PPPP', {
              locale: dateLocale,
            }),
          })}
        </Text>
      )}

      {!decidedInMeeting && <ProposalNonVotersNotice voters={state.voters} />}

      <Flex wrap="wrap" gap={3} justify="space-between" align="center">
        {decidedInMeeting ? (
          // In the meeting, those who may decide get the gestures. Everywhere
          // else — the tension read on its own — the proposal simply says what
          // it is waiting for, rather than inviting a decision out of session.
          readFromMeeting && state.canResolve ? (
            <ProposalResolveMeetingButtons />
          ) : (
            <Text fontSize="sm" color="gray.500" _dark={{ color: 'gray.300' }}>
              {t('ThreadActivityProposal.meetingPending')}
            </Text>
          )
        ) : (
          <HStack spacing={3}>
            <ProposalVoteButtons />
            <Text
              fontSize="sm"
              color="gray.500"
              _dark={{ color: 'gray.300' }}
              whiteSpace="nowrap"
            >
              {t('ThreadActivityProposal.voteProgress', {
                count: votes?.length || 0,
                total: state.votersCount,
              })}
            </Text>
          </HStack>
        )}

        {/* The manage menu stands down in a meeting: its entries either assume
            a vote, or duplicate what the three buttons already do — and
            cancel-and-recreate is precisely what left two proposals for one
            discussion. Outside a meeting it stays, the author still needs it. */}
        {canManage && !(decidedInMeeting && readFromMeeting) && (
          <ProposalManageMenu />
        )}
      </Flex>
    </Box>
  )
}
